import React from 'react';
import styled from 'styled-components';
import { theme } from '../../theme';
import { FaRunning, FaQuestion, FaTshirt, FaCloudRain, FaDumbbell, FaHandshake, FaPoundSign } from 'react-icons/fa';

// Forest movement image - people practicing natural movement in woods
const heroBackground = `${process.env.PUBLIC_URL}/images/kettlebells.jpg`;

const MovementCoachingContainer = styled.div`
  color: ${theme.colors.textLight};
`;

const HeroSection = styled.div`
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.5),
    rgba(${theme.colors.primaryRGB}, 0.8)
  ), url(${heroBackground}) no-repeat center center;
  background-size: cover;
  height: 90vh; 
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 0 20px;
  
  @media screen and (max-width: 768px) {
    height: 80vh;
  }
`;

const HeroContent = styled.div`
  max-width: 900px;
  margin-top: 80px;
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: bold;
  color: #fff;
  margin-bottom: 24px;
  letter-spacing: 1px;
  font-family: ${theme.fonts.heading};
  text-shadow: 2px 2px 4px rgba(0,0,0,0.5);
  
  @media screen and (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroTagline = styled.p`
  font-size: 1.5rem;
  color: ${theme.colors.light};
  margin: 0 auto 40px;
  font-style: italic;
  text-shadow: 1px 1px 3px rgba(0,0,0,0.5);
  
  @media screen and (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const ContentSection = styled.section`
  background: ${theme.colors.dark};
  padding: ${theme.spacing.section} 0;
`;

const SectionWrapper = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionHeading = styled.h2`
  font-size: 2.5rem;
  color: ${theme.colors.accent};
  margin-bottom: 40px;
  font-family: ${theme.fonts.heading};
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    width: 60px;
    height: 3px;
    background-color: ${theme.colors.accent};
    bottom: -10px;
    left: 0;
  }
`;

const Paragraph = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  color: ${theme.colors.textLight};
  margin-bottom: 25px;
`;

const Emphasis = styled.em`
  display: block;
  font-size: 1.2rem;
  margin: 30px 0;
  color: ${theme.colors.accent};
`;

// Component for image and text layout
const ContentWithImage = styled.div`
  display: flex;
  align-items: center;
  margin: 50px 0;
  flex-direction: ${props => props.imageRight ? 'row-reverse' : 'row'};
  
  @media screen and (max-width: 768px) {
    flex-direction: column;
  }
`;

const ImageContainer = styled.div`
  flex: 1;
  padding: ${props => props.imageRight ? '0 0 0 30px' : '0 30px 0 0'};
  
  @media screen and (max-width: 768px) {
    padding: 0;
    margin-bottom: 30px;
    width: 100%;
  }
`;

const ContentImage = styled.img`
  width: 100%;
  border-radius: ${theme.borderRadius};
  box-shadow: ${theme.boxShadow};
  object-fit: cover;
  height: 300px;
`;

const TextContent = styled.div`
  flex: 1;
`;

const TrainingOptionsContainer = styled.div`
  margin: 50px 0;
  background: ${theme.colors.primaryDark};
  padding: 30px;
  border-radius: ${theme.borderRadius};
  box-shadow: ${theme.boxShadow};
`;

const TrainingOptionsTitle = styled.h3`
  font-size: 1.8rem;
  color: ${theme.colors.accent};
  margin-bottom: 25px;
  text-align: center;
`;

const TrainingOptionsList = styled.ul`
  list-style: none;
  padding: 0;
`;

const TrainingOptionItem = styled.li`
  margin-bottom: 20px;
  font-size: 1.1rem;
  padding-left: 30px;
  position: relative;
  
  &:before {
    content: '✓';
    position: absolute;
    left: 0;
    color: ${theme.colors.accent};
  }
`;

const EthosStatement = styled.div`
  text-align: center;
  padding: 20px;
  margin: 40px 0;
  border-top: 1px solid ${theme.colors.accent};
  border-bottom: 1px solid ${theme.colors.accent};
`;

const EthosText = styled.p`
  font-size: 1.3rem;
  font-style: italic;
  color: ${theme.colors.accent};
  line-height: 1.8;
`;

const BookButton = styled.a`
  display: inline-block;
  background: ${theme.colors.accent};
  color: white;
  padding: 15px 30px;
  font-size: 1.1rem;
  border-radius: ${theme.borderRadius};
  text-decoration: none;
  margin: 30px 0;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${theme.colors.accentLight};
    transform: translateY(-3px);
  }
`;

const FAQSection = styled.section`
  background: linear-gradient(to bottom, ${theme.colors.primary}, ${theme.colors.primaryDark});
  padding: ${theme.spacing.section} 0;
`;

const FAQContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 20px;
`;

const FAQTitle = styled.h2`
  font-size: 2.5rem;
  color: ${theme.colors.textLight};
  margin-bottom: 50px;
  text-align: center;
  font-family: ${theme.fonts.heading};
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    width: 80px;
    height: 3px;
    background-color: ${theme.colors.accent};
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
  }
`;

const FAQItem = styled.div`
  margin-bottom: 30px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: ${theme.borderRadius};
  padding: 25px;
  backdrop-filter: blur(5px);
`;

const FAQQuestion = styled.h3`
  font-size: 1.3rem;
  color: ${theme.colors.accent};
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 15px;
  }
`;

const FAQAnswer = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
`;

const MovementCoachingSection = () => {
  return (
    <MovementCoachingContainer id="movement-coaching">
      <HeroSection>
        <HeroContent>
          <HeroTitle>Movement Coaching</HeroTitle>
          <HeroTagline>Functional strength and mobility — outside, in nature</HeroTagline>
        </HeroContent>
      </HeroSection>
      
      <ContentSection>
        <SectionWrapper>
          <SectionHeading>This isn't about chasing numbers or punishing workouts.</SectionHeading>
          
          <Paragraph>
            It's about moving well, feeling strong, and honouring the story your body carries.
          </Paragraph>
          
          <Paragraph>
            Through WildStrong Movement Coaching, I offer practical, functional movement sessions designed for longevity - not just aesthetics. 
            We train outdoors, using the landscape as our gym floor: crawling, climbing, balancing, lifting, throwing, and flowing with what nature gives us.
          </Paragraph>
          
          <ContentWithImage imageRight={true}>
            <ImageContainer imageRight={true}>
              <ContentImage 
                src="https://images.unsplash.com/photo-1551632811-561732d1e306?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80"
                alt="Person practicing natural movement outdoors" 
              />
            </ImageContainer>
            <TextContent>
              <Paragraph>
                Whether you're recovering, rebuilding, or just wanting to future-proof your body, these sessions are about making strength and mobility a natural part of life again.
              </Paragraph>
            </TextContent>
          </ContentWithImage>
          
          <TrainingOptionsContainer>
            <TrainingOptionsTitle>How You Can Train:</TrainingOptionsTitle>
            <TrainingOptionsList>
              <TrainingOptionItem>Group Drop-In Classes – regular outdoor sessions open to all levels.</TrainingOptionItem>
              <TrainingOptionItem>Thematic Block Programmes (4–8 weeks) – focused series like "Primal Strength," "Rewild Your Spine," or "Groundwork for Longevity."</TrainingOptionItem>
              <TrainingOptionItem>Online Movement Coaching – train with us remotely, wherever you are.</TrainingOptionItem>
            </TrainingOptionsList>
            <Paragraph style={{ marginTop: '20px' }}>
              All sessions are scalable, accessible, and built around natural human movement patterns.
            </Paragraph>
          </TrainingOptionsContainer>
          
          <EthosStatement>
            <EthosText>
              It's not about beating your body into submission.<br />
              It's about coming home to it.
            </EthosText>
          </EthosStatement>
          
          <ContentWithImage imageRight={false}>
            <ImageContainer>
              <ContentImage 
                src="https://images.unsplash.com/photo-1508962914676-134849a727f0?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80"
                alt="Group movement session in natural setting" 
              />
            </ImageContainer>
            <TextContent>
              <Paragraph>
                Our bodies were designed to move through natural environments. To climb, to crawl, to lift, to play. 
                Modern life has taken much of that away, but we can reclaim it.
              </Paragraph>
              <Paragraph>
                Join us for a session and rediscover what your body is truly capable of in a supportive, non-competitive environment.
              </Paragraph>
              <div style={{ textAlign: 'left' }}>
                <BookButton href="#contact">Book a Movement Session</BookButton>
              </div>
            </TextContent>
          </ContentWithImage>
        </SectionWrapper>
      </ContentSection>
      
      <FAQSection>
        <FAQContainer>
          <FAQTitle>Frequently Asked Questions</FAQTitle>
          
          <FAQItem>
            <FAQQuestion>
              <FaRunning /> Do I need to be fit to start?
            </FAQQuestion>
            <FAQAnswer>
              No. WildStrong coaching meets you where you're at. Whether you're a beginner or an experienced mover, 
              we adapt movements to your ability. Progress happens naturally.
            </FAQAnswer>
          </FAQItem>
          
          <FAQItem>
            <FAQQuestion>
              <FaTshirt /> What should I bring or wear?
            </FAQQuestion>
            <FAQAnswer>
              Loose, comfortable clothing you don't mind getting a bit muddy. Barefoot or minimalist shoes are 
              ideal (but not required). Bring water and a willingness to get a bit earthy.
            </FAQAnswer>
          </FAQItem>
          
          <FAQItem>
            <FAQQuestion>
              <FaCloudRain /> What happens if the weather's bad?
            </FAQQuestion>
            <FAQAnswer>
              We embrace the elements where possible (rain adds character!). Severe weather will move us to 
              a sheltered spot or reschedule if necessary. You'll always be informed in advance.
            </FAQAnswer>
          </FAQItem>
          
          <FAQItem>
            <FAQQuestion>
              <FaDumbbell /> Is this like CrossFit or bootcamp training?
            </FAQQuestion>
            <FAQAnswer>
              Nope. There are no ego lifts, no shouting, and no chasing reps for the sake of it. WildStrong 
              is about practical strength, resilience, mobility, and reconnecting with natural movement — 
              not performance for performance's sake.
            </FAQAnswer>
          </FAQItem>
          
          <FAQItem>
            <FAQQuestion>
              <FaHandshake /> Can I combine this with psychotherapy or other services?
            </FAQQuestion>
            <FAQAnswer>
              Absolutely. Movement and psychotherapy complement each other beautifully. Many people find that 
              building physical resilience supports emotional healing and vice versa.
            </FAQAnswer>
          </FAQItem>
          
          <FAQItem>
            <FAQQuestion>
              <FaPoundSign /> How much does it cost?
            </FAQQuestion>
            <FAQAnswer>
              Prices vary depending on whether you're joining drop-ins, booking a block programme, 
              or training online. Get in touch to find the option that fits you best. In some cases 
              the sessions are free to attend based on location.
            </FAQAnswer>
          </FAQItem>
        </FAQContainer>
      </FAQSection>
    </MovementCoachingContainer>
  );
};

export default MovementCoachingSection;